import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Container,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import toast, { Toaster } from "react-hot-toast";
import {
  useConfirmPayment,
  useStaffPermissions,
  useSuperAdminSubscriptions,
  useUpdateSubscription,
} from "../hooks";
import { useSaasPlans } from "../../plan/hooks";

const STATUSES = ["TRIAL", "ACTIVE", "PAST_DUE", "FROZEN", "CANCELED"];

const STATUS_VARIANTS = {
  TRIAL: "info",
  ACTIVE: "success",
  PAST_DUE: "warning",
  FROZEN: "dark",
  CANCELED: "secondary",
};

const STATUS_LABELS = {
  TRIAL: "Prueba",
  ACTIVE: "Activa",
  PAST_DUE: "Pago vencido",
  FROZEN: "Congelada",
  CANCELED: "Cancelada",
};

const EDIT_HEADING_ID = "edit-sub-modal-title";

function EditSubscriptionModal({ subscription, onHide }) {
  const { data: plans } = useSaasPlans();
  const [planId, setPlanId] = useState(subscription?.plan?.id ?? "");
  const [status, setStatus] = useState(subscription?.status ?? "");
  const updateSub = useUpdateSubscription();

  function handleSubmit(e) {
    e.preventDefault();
    updateSub.mutate(
      { id: subscription.id, plan_id: planId, status },
      {
        onSuccess: () => {
          toast.success("Suscripción actualizada.");
          onHide();
        },
      }
    );
  }

  return (
    <Modal
      show={Boolean(subscription)}
      onHide={onHide}
      aria-labelledby={EDIT_HEADING_ID}
      aria-modal="true"
    >
      <Modal.Header closeButton>
        <Modal.Title id={EDIT_HEADING_ID}>
          Editar suscripción — {subscription?.company_name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {updateSub.isError && (
          <Alert variant="danger" className="small">
            {updateSub.error?.response?.data?.detail ?? "Error al actualizar."}
          </Alert>
        )}
        <p className="small text-muted">
          Edición directa del super-admin (omite el flujo de solicitud/pago del
          dueño). Úsalo para correcciones manuales.
        </p>
        <Form id="edit-sub-form" onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Plan</Form.Label>
            <Form.Select
              value={planId}
              onChange={(e) => setPlanId(Number(e.target.value))}
              aria-label="Seleccionar plan"
            >
              {(plans ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Estado</Form.Label>
            <Form.Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Seleccionar estado"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s] ?? s}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={updateSub.isPending}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          form="edit-sub-form"
          disabled={updateSub.isPending}
          aria-label="Guardar cambios en la suscripción"
        >
          {updateSub.isPending ? <Spinner size="sm" aria-hidden="true" /> : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const COLUMNS = [
  {
    id: "company",
    header: "Academia",
    accessorFn: (row) => row.company_name ?? "—",
  },
  {
    id: "plan",
    header: "Plan",
    accessorFn: (row) => row.plan?.name ?? "—",
  },
  {
    accessorKey: "status",
    header: "Estado",
    cell: ({ row }) => {
      const s = row.original;
      return (
        <div>
          <Badge bg={STATUS_VARIANTS[s.status] ?? "secondary"}>
            {STATUS_LABELS[s.status] ?? s.status}
          </Badge>
          {s.pending_plan && (
            <div className="small text-primary mt-1">
              Cambio pendiente → {s.pending_plan.name}
            </div>
          )}
          {s.scheduled_plan && (
            <div className="small text-muted mt-1">
              Downgrade programado → {s.scheduled_plan.name}
            </div>
          )}
          {s.pending_blocks > 0 && (
            <div className="small text-primary mt-1">
              {s.pending_blocks} bloque(s) pendiente(s) de pago
            </div>
          )}
          {(s.status === "PAST_DUE" || s.status === "FROZEN") &&
            s.days_until_freeze != null && (
              <div className="small text-danger mt-1">
                {s.days_until_freeze > 0
                  ? `Congela en ${s.days_until_freeze} día(s)`
                  : "Congelamiento vencido"}
              </div>
            )}
        </div>
      );
    },
  },
  {
    accessorKey: "trial_ends_at",
    header: "Trial hasta",
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? new Date(v).toLocaleDateString("es-CO") : "—";
    },
  },
  { accessorKey: "billing_cycle", header: "Ciclo" },
  { id: "actions", header: "Acciones" },
];

// ¿Hay algo que confirmar? Un cambio de plan pendiente, bloques pendientes de
// pago, o una sub vencida/congelada.
function canConfirm(sub) {
  return (
    Boolean(sub.pending_plan) ||
    sub.pending_blocks > 0 ||
    sub.status === "PAST_DUE" ||
    sub.status === "FROZEN"
  );
}

function SubscriptionsPage() {
  const { data: subscriptions, isLoading, isError } = useSuperAdminSubscriptions();
  const confirmPayment = useConfirmPayment();
  // Gating de UX (TASK-22): el backend revalida con HasStaffPermission.
  // `confirm_payment` es un POST pero declara el verbo `update` en
  // staff_action_verbs, así que lo cubre el mismo permiso que "Editar":
  // FINANCE y PLATFORM_ADMIN sí; SUPPORT/SALES/READ_ONLY ven el estado, no el botón.
  const { can } = useStaffPermissions();
  const canUpdate = can("subscriptions", "update");
  const [editing, setEditing] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");

  // Sin permiso de escritura la columna "Acciones" quedaría vacía.
  const columns = useMemo(
    () => (canUpdate ? COLUMNS : COLUMNS.filter((c) => c.id !== "actions")),
    [canUpdate]
  );

  function handleConfirm(sub) {
    const parts = [];
    if (sub.pending_plan) parts.push(`plan ${sub.pending_plan.name}`);
    if (sub.pending_blocks > 0) parts.push(`${sub.pending_blocks} bloque(s)`);
    const detail = parts.length ? ` (${parts.join(" + ")})` : "";
    if (!window.confirm(`¿Confirmar el pago de ${sub.company_name}${detail}?`))
      return;
    confirmPayment.mutate(sub.id, {
      onSuccess: () => toast.success(`Pago confirmado — ${sub.company_name}.`),
      onError: (err) =>
        toast.error(err?.response?.data?.detail ?? "No se pudo confirmar el pago."),
    });
  }

  const table = useReactTable({
    data: subscriptions ?? [],
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 20 } },
  });

  if (isLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner aria-label="Cargando suscripciones" />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container className="py-4">
        <Alert variant="danger">No se pudo cargar las suscripciones.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Toaster position="top-right" />
      <h4 className="mb-3">Suscripciones</h4>

      <Form.Control
        className="mb-3"
        style={{ maxWidth: "340px" }}
        placeholder="Buscar academia..."
        value={globalFilter}
        onChange={(e) => setGlobalFilter(e.target.value)}
        aria-label="Buscar suscripción por academia"
      />

      <div className="table-responsive">
        <Table hover bordered size="sm" className="align-middle">
          <thead className="table-light">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  if (cell.column.id === "actions") {
                    // La columna entera solo existe si canUpdate (ver `columns`),
                    // así que aquí ya hay permiso de escritura. Lo que queda por
                    // decidir es la precondición de DATOS de cada control.
                    const sub = row.original;
                    return (
                      <td key={cell.id}>
                        <div className="d-flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => setEditing(sub)}
                            aria-label={`Editar suscripción de ${sub.company_name}`}
                          >
                            Editar
                          </Button>
                          {canConfirm(sub) && (
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleConfirm(sub)}
                              disabled={confirmPayment.isPending}
                              aria-label={`Confirmar pago de ${sub.company_name}`}
                            >
                              Confirmar pago
                            </Button>
                          )}
                        </div>
                      </td>
                    );
                  }
                  return (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="d-flex align-items-center gap-2 mt-2">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          aria-label="Página anterior"
        >
          ‹ Anterior
        </Button>
        <span className="small text-muted">
          Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
        </span>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          aria-label="Página siguiente"
        >
          Siguiente ›
        </Button>
      </div>

      {editing && canUpdate && (
        <EditSubscriptionModal
          subscription={editing}
          onHide={() => setEditing(null)}
        />
      )}
    </Container>
  );
}

export { SubscriptionsPage };
